import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { cloneData, isSameData } from '../dto/data-model-utils.js';
import {
  SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { Country } from './country.js';
import { LocalizedString } from './localized-string.js';

export class OrganizerInfo implements ICloneable, IDataComparable {
  private constructor(
    public readonly uniqueId: string,
    public readonly webRoute: string,
    public readonly localizedName: LocalizedString,
    public readonly coverImage: string,
    public readonly country: Country,
    public readonly eventCount: number,
    public readonly isFeatured: boolean,
    public readonly featuredScore: number,
  ) {}

  static create(args: {
    uniqueId: string,
    webRoute: string,
    localizedName: LocalizedString,
    coverImage: string,
    country: Country,
    eventCount: number,
    isFeatured: boolean,
    featuredScore: number,
  }): OrganizerInfo {
    return new OrganizerInfo(
        args.uniqueId,
        args.webRoute,
        args.localizedName,
        args.coverImage,
        args.country,
        args.eventCount,
        args.isFeatured,
        args.featuredScore,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OrganizerInfo {
    const isFeatured = MapUtils.getBooleanOrNull(map, 'isFeatured') ?? false;
    const featuredScore = MapUtils.getIntegerOrNull(map, 'featuredScore') ?? 0;

    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      webRoute: MapUtils.getString(map, 'webRoute'),
      localizedName: context.fromData<LocalizedString>(MapUtils.getRecord(map, 'localizedName')),
      coverImage: MapUtils.getString(map, 'coverImage'),
      country: toEnumFromString(MapUtils.getString(map, 'country'), Country),
      eventCount: MapUtils.getInteger(map, 'eventCount'),
      isFeatured: isFeatured,
      featuredScore: featuredScore,
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      webRoute: this.webRoute,
      localizedName: context.toData(this.localizedName),
      coverImage: this.coverImage,
      country: fromEnumAsString(this.country),
      eventCount: this.eventCount,
      isFeatured: this.isFeatured,
      featuredScore: this.featuredScore,
    };
  }

  clone(): this {
    return OrganizerInfo.create({
      uniqueId: this.uniqueId,
      webRoute: this.webRoute,
      localizedName: cloneData(this.localizedName),
      coverImage: this.coverImage,
      country: this.country,
      eventCount: this.eventCount,
      isFeatured: this.isFeatured,
      featuredScore: this.featuredScore,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return (
      other.uniqueId === this.uniqueId &&
      other.webRoute === this.webRoute &&
      isSameData(other.localizedName, this.localizedName) &&
      other.coverImage === this.coverImage &&
      other.country === this.country &&
      other.eventCount === this.eventCount &&
      other.isFeatured === this.isFeatured &&
      other.featuredScore === this.featuredScore
    );
  }
}
